import { SECURITY_TRIGGERS } from "../constants/analytics";

const isSsr = typeof window === "undefined";

/**
 * Fires the page-load trigger on the replaying `lifeCycle` channel, so that
 * subscribers registering after load still receive it. No payload — the
 * listener owns the whole page object.
 */
export const triggerPageLoad = () => {
  if (isSsr) return;

  window?.LIFE_CYCLE_EVENT_BUS?.lifeCycle?.trigger(SECURITY_TRIGGERS.PAGE_LOAD);
};

/**
 * Fires the page-error trigger on the replaying `lifeCycle` channel. The
 * listener supplies type, displayType and pageSection.
 *
 * @param message - message shown to the user
 * @param code - error code from the API
 */
export const triggerPageError = ({ message, code }: { message: string; code: string }) => {
  if (isSsr) return;

  window?.LIFE_CYCLE_EVENT_BUS?.lifeCycle?.trigger(SECURITY_TRIGGERS.PAGE_ERROR, { message, code });
};

/**
 * Fires a stash event on the plain (non-replaying) channel — a discrete
 * interaction replayed to late subscribers would be double-counted.
 *
 * @param identifier - settings label, used verbatim by the listener
 */
export const triggerStashEvent = (identifier: string) => {
  if (isSsr) return;

  window?.LIFE_CYCLE_EVENT_BUS?.trigger(SECURITY_TRIGGERS.STASH_EVENT, { identifier });
};

/**
 * Fires a button-submission event on the plain (non-replaying) channel.
 *
 * @param eventName - used verbatim by the listener as eventInfo.eventName
 */
export const triggerSubmissionEvent = (eventName: string) => {
  if (isSsr) return;

  window?.LIFE_CYCLE_EVENT_BUS?.trigger(SECURITY_TRIGGERS.SUBMISSION_EVENT, { eventName });
};

/**
 * Fires a settings-submission event on the plain (non-replaying) channel — a
 * discrete change replayed to late subscribers would be double-counted.
 *
 * Unlike triggerSubmissionEvent, this feeds myAccount.settings; the listener
 * supplies eventInfo.eventName itself.
 *
 * @param settings - full label, used verbatim — colon spacing included
 */
export const triggerSettingsSubmissionEvent = (settings: string) => {
  if (isSsr) return;

  window?.LIFE_CYCLE_EVENT_BUS?.trigger(SECURITY_TRIGGERS.SETTINGS_SUBMISSION_EVENT, { settings });
};

/**
 * Fires the WebAuthn settings-submission event on the plain (non-replaying)
 * channel. No payload — the listener owns the whole object, including
 * myAccount.biometricEnablement, which is always "completed".
 *
 * Note it is biometricEnablement, not the myAccount.settings that every other
 * button-submission event writes. This is the only event in the app using that
 * field, so do not reach for triggerSettingsSubmissionEvent here.
 */
export const triggerWebauthnSettingsEvent = () => {
  if (isSsr) return;

  window?.LIFE_CYCLE_EVENT_BUS?.trigger(SECURITY_TRIGGERS.WEBAUTHN_SETTINGS_EVENT);
};

/**
 * Fires a post-page-load API error on the plain (non-replaying) channel.
 *
 * Unlike triggerPageError, pageSection travels in the payload rather than being
 * supplied by the listener: a page error has one section, but these fire from 14
 * distinct sections. The listener still owns type, displayType and eventName.
 *
 * @param pageSection - section identifier, used verbatim — casing included
 * @param code - error code from the API
 * @param message - message shown to the user
 */
export const triggerErrorEvent = (
  { pageSection, code, message }: { pageSection: string; code: string; message: string }
) => {
  if (isSsr) return;

  window?.LIFE_CYCLE_EVENT_BUS?.trigger(SECURITY_TRIGGERS.ERROR_EVENT, { pageSection, code, message });
};

/**
 * Fires a modal/overlay view event on the plain (non-replaying) channel — a
 * modal opening replayed to late subscribers would be double-counted.
 *
 * The listener owns category.primaryCategory ("overlay") and uses eventName
 * verbatim as eventInfo.eventName. There is no myAccount key on this event.
 *
 * @param eventName - full overlay label, used verbatim — hyphen spacing included
 */
export const triggerOverlayEvent = (eventName: string) => {
  if (isSsr) return;

  window?.LIFE_CYCLE_EVENT_BUS?.trigger(SECURITY_TRIGGERS.OVERLAY_EVENT, { eventName });
};
